import { Suspense, forwardRef, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { FormikProps } from "formik"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import { MERCHANT_ADMIN_EXTRA_SERVICES_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { CreateUpdateExtraServiceForm } from "../components/CreateUpdateExtraServiceForm"
import { CreateUpdateExtraServiceFormValues } from "../extraServicesTypes"
import { createExtraService, ExtraServicesQuery } from "../extraServicesApi"

const ExtraService = forwardRef<FormikProps<CreateUpdateExtraServiceFormValues>>((props, ref) => {
    const navigate = useNavigate()

    const { mutateAsync: callCreateExtraService } = useApiMutation(createExtraService, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ExtraServicesQuery.LIST] })
            navigate(MERCHANT_ADMIN_EXTRA_SERVICES_ROUTE)
        },
    })

    return (
        <>
            <CreateUpdateExtraServiceForm
                ref={ref}
                onSubmit={(data) => {
                    callCreateExtraService({ product: data })
                }}
            />
        </>
    )
})

export function CreateExtraServicePage() {
    const productFormRef = useRef<FormikProps<CreateUpdateExtraServiceFormValues>>(null)

    const handleSaveClick = () => {
        if (productFormRef.current) {
            productFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="extra-services-create-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_PRODUCTS}
                    title="Create extra service"
                    rightElement={
                        <LocalizedStrict id="product-create-submit-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-extra-services">
                            <BreadcrumbLink to={MERCHANT_ADMIN_EXTRA_SERVICES_ROUTE}>Extra services</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-extra-service-create">
                            <Breadcrumb>Create extra service</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <ExtraService ref={productFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
